// ARView — Realidad Aumentada
// Full-screen camera overlay with POI labels positioned via GPS bearing + device compass.
// Optional ?target=<placeId> enables turn-by-turn arrow mode toward a specific POI.
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { X, Compass, Navigation, AlertTriangle, Camera, Moon, Sun, Share2, Download, RotateCcw } from "lucide-react";
import { useTrip } from "../auth/TripContext";
import { useGeolocation, distanceMeters, formatDistance, walkingMinutes } from "../hooks/useGeolocation";
import { useDeviceHeading, bearingDegrees, angularDelta } from "../hooks/useDeviceHeading";

// Horizontal field of view used to project POIs onto the screen.
// Rear iPhone camera ≈ 60-70° in portrait; use 60° as a safe default.
const FOV_DEG = 60;
// Max distance to show a POI label (meters) — avoids cluttering with far-away ones.
const MAX_DISTANCE_M = 2500;
// Night-mode local hours (inclusive start, exclusive end). 20:00–02:00.
const NIGHT_START_HOUR = 20;
const NIGHT_END_HOUR = 2;
// Vertical screen offset for POI rows based on distance buckets (closer = lower).
function verticalForDistance(d) {
  if (d < 150) return 72;
  if (d < 400) return 60;
  if (d < 800) return 48;
  if (d < 1500) return 36;
  return 24;
}

function isLocalNightHour(h = new Date().getHours()) {
  return h >= NIGHT_START_HOUR || h < NIGHT_END_HOUR;
}

// Try to parse an "open now" boolean from a hours string like "Lun–Sáb 12:00–23:00".
// Conservative: returns null if ambiguous.
function parseOpenNow(hoursStr, now = new Date()) {
  if (!hoursStr || typeof hoursStr !== "string") return null;
  const m = hoursStr.match(/(\d{1,2}):(\d{2})\s*[–\-]\s*(\d{1,2}):(\d{2})/);
  if (!m) return null;
  const [, sh, sm, eh, em] = m;
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const startMin = parseInt(sh) * 60 + parseInt(sm);
  let endMin = parseInt(eh) * 60 + parseInt(em);
  // Overnight ranges (e.g. 19:00–01:00) → add 24h to end
  const overnight = endMin <= startMin;
  if (overnight) {
    if (nowMin >= startMin) return true;
    return nowMin < endMin;
  }
  return nowMin >= startMin && nowMin < endMin;
}

export default function ARView() {
  const navigate = useNavigate();
  const { tripId } = useParams();
  const [searchParams] = useSearchParams();
  const targetId = searchParams.get("target");
  const { trip } = useTrip();

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [camState, setCamState] = useState("idle"); // idle | requesting | granted | denied | unsupported
  const [camError, setCamError] = useState("");

  const { position, permission: gpsPermission } = useGeolocation();
  const { heading, permission: compassPermission, requestPermission: requestCompass, supported: compassSupported } = useDeviceHeading();
  const [starting, setStarting] = useState(false);
  // Night mode (auto-on at local 20h-02h, user togglable).
  const [nightMode, setNightMode] = useState(() => isLocalNightHour());
  // Photo capture state: null | { url, blob }
  const [capture, setCapture] = useState(null);
  const [sharing, setSharing] = useState(false);

  // Build POI list: day mode → places + hotels; night mode → restaurants + food places + events.
  const pois = useMemo(() => {
    const list = [];
    if (!nightMode) {
      const places = trip?.places || {};
      Object.values(places).forEach((p) => {
        if (p && p.lat != null && p.lng != null) {
          list.push({
            id: p.id,
            name: p.name,
            city: p.city,
            icon: p.icon || "📍",
            lat: p.lat,
            lng: p.lng,
            kind: p.jewish ? "jewish" : p.type || "place",
            href: `/viaje/${tripId}/lugar/${p.id}`,
            meta: null,
            story: p.story || p.description || p.tip || "",
          });
        }
      });
      (trip?.hotels || []).forEach((h) => {
        if (h && h.lat != null && h.lng != null) {
          list.push({
            id: `h-${h.id}`,
            name: h.name,
            city: h.city,
            icon: "🏨",
            lat: h.lat,
            lng: h.lng,
            kind: "hotel",
            href: `/viaje/${tripId}/logistica`,
            meta: null,
            story: h.notes || h.description || "",
          });
        }
      });
      return list;
    }
    // NIGHT MODE
    const rests = trip?.restaurants || {};
    const pushRest = (r) => {
      if (!r || r.lat == null || r.lng == null) return;
      list.push({
        id: `r-${r.id || r.name}`,
        name: r.name,
        city: r.city || r.area,
        icon: r.icon || "🍽️",
        lat: r.lat,
        lng: r.lng,
        kind: "food",
        href: `/viaje/${tripId}/sabores`,
        meta: { cuisine: r.cuisine || "", hours: r.hours || "", openNow: parseOpenNow(r.hours) },
        story: r.tip || r.signature || r.description || "",
      });
    };
    if (Array.isArray(rests)) rests.forEach(pushRest);
    else Object.values(rests).forEach((arr) => (arr || []).forEach(pushRest));
    const places = trip?.places || {};
    Object.values(places).forEach((p) => {
      if (!p || p.lat == null || p.lng == null) return;
      const isFood = p.type === "food" || p.type === "bar" || p.type === "nightlife";
      if (!isFood) return;
      list.push({
        id: p.id,
        name: p.name,
        city: p.city,
        icon: p.icon || "🍸",
        lat: p.lat,
        lng: p.lng,
        kind: "food",
        href: `/viaje/${tripId}/lugar/${p.id}`,
        meta: { cuisine: p.cuisine || p.type || "", hours: p.hours || "", openNow: parseOpenNow(p.hours) },
        story: p.story || p.tip || p.description || "",
      });
    });
    (trip?.events || []).forEach((e) => {
      if (!e || e.lat == null || e.lng == null) return;
      list.push({
        id: `e-${e.id || e.name}`,
        name: e.name || e.title,
        city: e.city,
        icon: e.icon || "🎭",
        lat: e.lat,
        lng: e.lng,
        kind: "event",
        href: `/viaje/${tripId}/eventos`,
        meta: { cuisine: e.date || "Hoy", hours: e.time || "", openNow: null },
        story: e.description || e.story || e.notes || "",
      });
    });
    return list;
  }, [trip, tripId, nightMode]);

  // Compute visible POIs with bearing + distance.
  const annotated = useMemo(() => {
    if (!position) return [];
    return pois
      .map((p) => {
        const d = distanceMeters(position, { lat: p.lat, lng: p.lng });
        const b = bearingDegrees(position, { lat: p.lat, lng: p.lng });
        return { ...p, distance: d, bearing: b };
      })
      .filter((p) => p.distance != null && p.distance <= MAX_DISTANCE_M)
      .sort((a, b) => a.distance - b.distance);
  }, [pois, position]);

  // Target POI for navigation arrow (if ?target=id passed).
  const targetPoi = useMemo(() => {
    if (!targetId) return null;
    return annotated.find((p) => p.id === targetId || p.id === `h-${targetId}`) || null;
  }, [targetId, annotated]);

  // Current visible POIs (within FOV ahead of us).
  const visible = useMemo(() => {
    if (heading == null) return [];
    return annotated
      .map((p) => ({ ...p, delta: angularDelta(p.bearing, heading) }))
      .filter((p) => p.delta != null && Math.abs(p.delta) <= FOV_DEG / 2 + 5)
      .slice(0, 8); // cap to avoid overlap
  }, [annotated, heading]);

  async function startAR() {
    setStarting(true);
    setCamError("");
    try {
      // 1) Compass permission (iOS gesture-triggered)
      if (compassSupported) {
        const compassState = await requestCompass();
        if (compassState === "denied") {
          setCamError("Necesitamos la brújula para orientar los puntos. Activala en Ajustes → Safari → Movimiento y orientación.");
          setStarting(false);
          return;
        }
      }
      // 2) Camera stream
      setCamState("requesting");
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setCamState("unsupported");
        setStarting(false);
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        try { await videoRef.current.play(); } catch { /* ignore */ }
      }
      setCamState("granted");
    } catch (err) {
      setCamState("denied");
      setCamError(err?.message || "No se pudo acceder a la cámara.");
    } finally {
      setStarting(false);
    }
  }

  function stopAR() {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
  }

  useEffect(() => {
    return () => stopAR();
  }, []);

  function close() {
    stopAR();
    navigate(`/viaje/${tripId}/map`);
  }

  // --- Photo capture ---
  // Freeze the current video frame to an offscreen canvas so re-renders (e.g. toggling
  // story overlay) are deterministic and don't drift with the live stream.
  const baseFrameRef = useRef(null); // { canvas, dpr, outW, outH }

  function roundRectPath(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }

  function wrapText(ctx, text, maxW) {
    const words = String(text || "").split(/\s+/).filter(Boolean);
    const lines = [];
    let line = "";
    for (const w of words) {
      const test = line ? line + " " + w : w;
      if (ctx.measureText(test).width > maxW && line) {
        lines.push(line);
        line = w;
      } else {
        line = test;
      }
    }
    if (line) lines.push(line);
    return lines;
  }

  function renderComposed(base, poisSnap, storyPoiId) {
    const outW = base.outW, outH = base.outH, dpr = base.dpr;
    const canvas = document.createElement("canvas");
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(base.canvas, 0, 0);

    // Darken top + bottom (matches on-screen gradient).
    const grad = ctx.createLinearGradient(0, 0, 0, outH);
    grad.addColorStop(0, "rgba(0,0,0,0.55)");
    grad.addColorStop(0.25, "rgba(0,0,0,0)");
    grad.addColorStop(0.65, "rgba(0,0,0,0)");
    grad.addColorStop(1, "rgba(0,0,0,0.6)");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, outW, outH);

    // POI labels
    poisSnap.forEach((p) => {
      const cx = (xPercent(p.delta) / 100) * outW;
      const cy = (verticalForDistance(p.distance) / 100) * outH;
      const scale = (p.distance < 200 ? 1.0 : p.distance < 600 ? 0.9 : 0.8) * dpr;
      const r = 22 * scale;
      ctx.save();
      ctx.translate(cx, cy);
      ctx.beginPath();
      ctx.arc(0, 0, r, 0, Math.PI * 2);
      const isTarget = targetPoi && p.id === targetPoi.id;
      const isStoryPoi = storyPoiId && p.id === storyPoiId;
      ctx.fillStyle = isTarget || isStoryPoi
        ? "#d2691e"
        : p.kind === "jewish"
          ? "#e5b53a"
          : p.kind === "food"
            ? "#ffffff"
            : p.kind === "hotel"
              ? "#2d4a73"
              : p.kind === "event"
                ? "#8b5cf6"
                : "#ffffff";
      ctx.fill();
      ctx.lineWidth = (isStoryPoi ? 3.5 : 2.5) * dpr;
      ctx.strokeStyle = isTarget || isStoryPoi || p.kind === "food" ? "#d2691e" : "rgba(255,255,255,0.9)";
      ctx.stroke();
      ctx.font = `${20 * scale}px "Apple Color Emoji","Segoe UI Emoji",-apple-system,sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillStyle = "#000";
      ctx.fillText(p.icon || "📍", 0, 0);

      const fontSize = 11 * scale;
      const name = p.name && p.name.length > 24 ? p.name.slice(0, 22) + "…" : (p.name || "");
      const subText = `${formatDistance(p.distance)} · ${walkingMinutes(p.distance)} min`;
      ctx.font = `600 ${fontSize}px -apple-system,"Segoe UI",sans-serif`;
      const nameW = ctx.measureText(name).width;
      ctx.font = `500 ${fontSize * 0.85}px -apple-system,"Segoe UI",sans-serif`;
      const subW = ctx.measureText(subText).width;
      const pillW = Math.max(nameW, subW) + 16 * scale;
      const pillH = fontSize * 2.4 + 10 * scale;
      const pillX = -pillW / 2;
      const pillY = r + 8 * scale;
      roundRectPath(ctx, pillX, pillY, pillW, pillH, 8 * scale);
      ctx.fillStyle = "rgba(0,0,0,0.78)";
      ctx.fill();
      ctx.lineWidth = 1 * scale;
      ctx.strokeStyle = "rgba(255,255,255,0.15)";
      ctx.stroke();
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      ctx.font = `600 ${fontSize}px -apple-system,"Segoe UI",sans-serif`;
      ctx.fillStyle = "#ffffff";
      ctx.fillText(name, 0, pillY + 5 * scale);
      ctx.font = `500 ${fontSize * 0.85}px -apple-system,"Segoe UI",sans-serif`;
      ctx.fillStyle = "rgba(255,255,255,0.75)";
      ctx.fillText(subText, 0, pillY + fontSize + 8 * scale);
      ctx.restore();
    });

    // Story panel (optional)
    const storyPoi = storyPoiId ? poisSnap.find((p) => p.id === storyPoiId) : null;
    if (storyPoi && storyPoi.story) {
      const padX = 20 * dpr;
      const panelW = outW - padX * 2;
      const panelX = padX;
      const bodySize = 13 * dpr;
      const titleSize = 11 * dpr;
      const lineH = bodySize * 1.4;
      const maxTextW = panelW - 28 * dpr;
      // Trim story to ~260 chars so it fits nicely
      let story = String(storyPoi.story).replace(/\s+/g, " ").trim();
      if (story.length > 260) story = story.slice(0, 257).replace(/\s\S*$/, "") + "…";
      ctx.font = `500 ${bodySize}px -apple-system,"Georgia",serif`;
      const lines = wrapText(ctx, story, maxTextW).slice(0, 5); // max 5 lines
      const titleGap = 10 * dpr;
      const panelH = 14 * dpr + titleSize + titleGap + lines.length * lineH + 14 * dpr;
      const panelY = outH - panelH - 70 * dpr; // leave room for watermark

      roundRectPath(ctx, panelX, panelY, panelW, panelH, 16 * dpr);
      ctx.fillStyle = "rgba(0,0,0,0.78)";
      ctx.fill();
      ctx.lineWidth = 1 * dpr;
      ctx.strokeStyle = "rgba(255,255,255,0.18)";
      ctx.stroke();
      // Accent bar
      ctx.fillStyle = "#d2691e";
      ctx.fillRect(panelX, panelY + 14 * dpr, 3 * dpr, titleSize);
      // Title
      ctx.textAlign = "left";
      ctx.textBaseline = "top";
      ctx.font = `700 ${titleSize}px -apple-system,"Segoe UI",sans-serif`;
      ctx.fillStyle = "rgba(210, 105, 30, 0.95)";
      const titleName = storyPoi.name && storyPoi.name.length > 36 ? storyPoi.name.slice(0, 34) + "…" : (storyPoi.name || "");
      ctx.fillText(titleName.toUpperCase(), panelX + 14 * dpr, panelY + 14 * dpr);
      // Body
      ctx.font = `500 ${bodySize}px -apple-system,"Georgia",serif`;
      ctx.fillStyle = "rgba(255,255,255,0.95)";
      lines.forEach((line, i) => {
        ctx.fillText(line, panelX + 14 * dpr, panelY + 14 * dpr + titleSize + titleGap + i * lineH);
      });
    }

    // Bitácora watermark bottom-right
    ctx.save();
    ctx.font = `700 ${13 * dpr}px -apple-system,"Georgia",serif`;
    ctx.textAlign = "right";
    ctx.textBaseline = "bottom";
    ctx.shadowColor = "rgba(0,0,0,0.6)";
    ctx.shadowBlur = 6 * dpr;
    ctx.fillStyle = "rgba(255,255,255,0.95)";
    ctx.fillText("Bitácora", outW - 20 * dpr, outH - 42 * dpr);
    ctx.font = `500 ${10 * dpr}px -apple-system,"Segoe UI",sans-serif`;
    ctx.fillStyle = "rgba(255,255,255,0.75)";
    ctx.fillText(nightMode ? "Modo AR · Noche" : "Modo AR", outW - 20 * dpr, outH - 22 * dpr);
    ctx.restore();

    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.92);
    });
  }

  async function capturePhoto() {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const outW = Math.round(window.innerWidth * dpr);
    const outH = Math.round(window.innerHeight * dpr);

    // Snapshot the pure video frame once — reused across story-toggle re-renders.
    const base = document.createElement("canvas");
    base.width = outW;
    base.height = outH;
    const bctx = base.getContext("2d");
    const vw = video.videoWidth, vh = video.videoHeight;
    const videoAspect = vw / vh;
    const outAspect = outW / outH;
    let sx, sy, sWidth, sHeight;
    if (videoAspect > outAspect) {
      sHeight = vh;
      sWidth = vh * outAspect;
      sx = (vw - sWidth) / 2;
      sy = 0;
    } else {
      sWidth = vw;
      sHeight = vw / outAspect;
      sx = 0;
      sy = (vh - sHeight) / 2;
    }
    bctx.drawImage(video, sx, sy, sWidth, sHeight, 0, 0, outW, outH);
    baseFrameRef.current = { canvas: base, dpr, outW, outH };

    const poisSnap = visible.slice();
    const blob = await renderComposed(baseFrameRef.current, poisSnap, null);
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    setCapture({ url, blob, poisSnap, storyPoiId: null });
  }

  async function setStoryPoi(nextStoryPoiId) {
    if (!capture || !baseFrameRef.current) return;
    const blob = await renderComposed(baseFrameRef.current, capture.poisSnap, nextStoryPoiId);
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    setCapture((prev) => {
      if (prev?.url) URL.revokeObjectURL(prev.url);
      return { ...prev, url, blob, storyPoiId: nextStoryPoiId };
    });
  }

  async function sharePhoto() {
    if (!capture?.blob) return;
    setSharing(true);
    try {
      const filename = `bitacora-ar-${Date.now()}.jpg`;
      const file = new File([capture.blob], filename, { type: "image/jpeg" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "Bitácora",
          text: nightMode ? "Noche en el viaje 🌙" : "Dónde estoy ahora mismo 🧭",
        });
      } else {
        // Fallback: download
        downloadPhoto();
      }
    } catch {
      // User cancelled share — no-op
    } finally {
      setSharing(false);
    }
  }

  function downloadPhoto() {
    if (!capture?.url) return;
    const a = document.createElement("a");
    a.href = capture.url;
    a.download = `bitacora-ar-${Date.now()}.jpg`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  function discardPhoto() {
    if (capture?.url) URL.revokeObjectURL(capture.url);
    baseFrameRef.current = null;
    setCapture(null);
  }

  // Horizontal screen position (%) for a POI based on its angular delta.
  function xPercent(delta) {
    const halfFov = FOV_DEG / 2;
    const clamped = Math.max(-halfFov, Math.min(halfFov, delta));
    return 50 + (clamped / halfFov) * 45; // keep 5% margin on edges
  }

  const canShowAR = camState === "granted";
  const headingLabel = heading == null ? "—" : `${Math.round(heading)}°`;
  const gpsReady = gpsPermission === "granted" && !!position;

  return (
    <div className="fixed inset-0 bg-black z-[100]" data-testid="ar-view">
      {/* Video stream */}
      <video
        ref={videoRef}
        playsInline
        muted
        autoPlay
        className="absolute inset-0 w-full h-full object-cover"
        style={{ background: "#000" }}
      />

      {/* Dark gradient to make text readable */}
      {canShowAR && (
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: "linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 25%, rgba(0,0,0,0) 65%, rgba(0,0,0,0.6) 100%)" }} />
      )}

      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 px-4 pt-[max(env(safe-area-inset-top),16px)] pb-3 flex items-center gap-2 z-20">
        <button
          onClick={close}
          className="w-10 h-10 rounded-full bg-black/60 backdrop-blur-md border border-white/20 text-white flex items-center justify-center active:scale-95 transition-transform"
          data-testid="ar-close-btn"
          aria-label="Cerrar AR"
        >
          <X size={20} />
        </button>
        <div className="flex-1" />
        {canShowAR && (
          <button
            onClick={() => setNightMode((v) => !v)}
            className={`h-10 pl-3 pr-3.5 rounded-full backdrop-blur-md border text-xs flex items-center gap-1.5 active:scale-95 transition-transform ${
              nightMode
                ? "bg-[hsl(260_60%_55%)]/85 border-white/30 text-white"
                : "bg-black/60 border-white/20 text-white"
            }`}
            data-testid="ar-night-toggle"
            aria-label="Alternar modo noche"
          >
            {nightMode ? <Moon size={14} fill="currentColor" /> : <Sun size={14} />}
            <span className="font-semibold">{nightMode ? "Noche" : "Día"}</span>
          </button>
        )}
        {canShowAR && (
          <div className="h-10 px-3 rounded-full bg-black/60 backdrop-blur-md border border-white/20 text-white text-xs flex items-center gap-1.5" data-testid="ar-heading-chip">
            <Compass size={14} className="opacity-80" />
            <span className="tabular-nums">{headingLabel}</span>
          </div>
        )}
      </div>

      {/* Initial permission screen */}
      {!canShowAR && (
        <div className="absolute inset-0 flex items-center justify-center p-6 z-10">
          <div className="w-full max-w-md rounded-3xl bg-[hsl(var(--bg))] p-6 border border-[hsl(var(--line))] shadow-xl" data-testid="ar-intro-card">
            <div className="w-14 h-14 rounded-2xl bg-[hsl(var(--accent)/0.12)] text-[hsl(var(--accent))] flex items-center justify-center mb-4">
              <Navigation size={26} />
            </div>
            <h2 className="text-2xl font-bold leading-tight mb-2">Modo AR del viaje</h2>
            <p className="text-sm text-[hsl(var(--muted))] leading-relaxed mb-4">
              Apuntá con la cámara hacia la calle y vas a ver los lugares de tu itinerario flotando sobre el mundo real, con nombre y distancia. Tocá uno para abrir su historia, o sacá una foto para compartir.
            </p>
            {nightMode && (
              <div className="mb-4 p-2.5 rounded-xl bg-[hsl(260_60%_55%)/0.1] border border-[hsl(260_60%_55%)/0.3] text-[11px] text-[hsl(260_50%_35%)] flex items-center gap-2" data-testid="ar-night-hint">
                <Moon size={13} fill="currentColor" />
                <span>Modo noche activo — vas a ver bares, restaurantes y eventos. Podés cambiar a modo día desde el chip de arriba.</span>
              </div>
            )}
            <ul className="text-xs text-[hsl(var(--muted))] space-y-1.5 mb-5">
              <li className="flex items-start gap-2"><span>📷</span> Permiso de cámara (solo mientras lo usás)</li>
              <li className="flex items-start gap-2"><span>🧭</span> Brújula para orientar los puntos</li>
              <li className="flex items-start gap-2"><span>📍</span> GPS {gpsReady ? "— ya activo" : "para saber dónde estás parado"}</li>
            </ul>
            {camError && (
              <div className="mb-4 p-3 rounded-xl bg-[hsl(0_75%_95%)] border border-[hsl(0_60%_85%)] text-xs text-[hsl(0_60%_35%)] flex items-start gap-2" data-testid="ar-error">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                <span>{camError}</span>
              </div>
            )}
            <button
              onClick={startAR}
              disabled={starting}
              className="w-full py-3 rounded-2xl bg-[hsl(var(--ink))] text-[hsl(var(--bg))] font-semibold active:scale-[0.98] disabled:opacity-60"
              data-testid="ar-start-btn"
            >
              {starting ? "Activando…" : "Activar modo AR"}
            </button>
            <button
              onClick={close}
              className="w-full py-2 mt-2 text-sm text-[hsl(var(--muted))]"
              data-testid="ar-cancel-btn"
            >
              Volver al mapa
            </button>
          </div>
        </div>
      )}

      {/* GPS warning overlay when AR is active but no GPS yet */}
      {canShowAR && !gpsReady && (
        <div className="absolute left-4 right-4 top-20 z-20">
          <div className="rounded-xl bg-black/70 backdrop-blur-md border border-white/15 text-white text-xs px-3 py-2 text-center" data-testid="ar-gps-warning">
            {gpsPermission === "denied" ? "📍 GPS denegado — no puedo ubicarte." : "📍 Buscando tu ubicación…"}
          </div>
        </div>
      )}

      {/* Compass warning (unlikely once started, but handle gracefully) */}
      {canShowAR && gpsReady && heading == null && (
        <div className="absolute left-4 right-4 top-20 z-20">
          <div className="rounded-xl bg-black/70 backdrop-blur-md border border-white/15 text-white text-xs px-3 py-2 text-center" data-testid="ar-compass-warning">
            🧭 Mové un poco el teléfono para calibrar la brújula…
          </div>
        </div>
      )}

      {/* POI floating labels */}
      {canShowAR && gpsReady && heading != null && visible.map((p) => {
        const x = xPercent(p.delta);
        const y = verticalForDistance(p.distance);
        const isTarget = targetPoi && p.id === targetPoi.id;
        const scale = p.distance < 200 ? 1.0 : p.distance < 600 ? 0.9 : 0.8;
        return (
          <button
            key={p.id}
            onClick={() => { stopAR(); navigate(p.href); }}
            className={`absolute z-10 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-1 active:scale-95 transition-transform ${isTarget ? "" : ""}`}
            style={{ left: `${x}%`, top: `${y}%`, transform: `translate(-50%, -50%) scale(${scale})` }}
            data-testid={`ar-poi-${p.id}`}
          >
            <div className={`w-11 h-11 rounded-full border-2 flex items-center justify-center text-xl shadow-lg ${
              isTarget
                ? "bg-[hsl(var(--accent))] border-white"
                : p.kind === "jewish"
                  ? "bg-[hsl(45_80%_55%)] border-white/80"
                  : p.kind === "food"
                    ? "bg-white border-[hsl(var(--accent))]"
                    : p.kind === "hotel"
                      ? "bg-[hsl(215_60%_35%)] border-white/80"
                      : p.kind === "event"
                        ? "bg-[hsl(260_60%_55%)] border-white/80"
                        : "bg-white border-black/60"
            }`}>
              <span>{p.icon}</span>
            </div>
            <div className="px-2.5 py-1 rounded-lg bg-black/75 backdrop-blur-sm border border-white/15 max-w-[170px]">
              <p className="text-white text-[11px] font-semibold leading-tight truncate">{p.name}</p>
              <p className="text-white/70 text-[10px] tabular-nums leading-tight">{formatDistance(p.distance)} · {walkingMinutes(p.distance)} min</p>
              {p.meta?.cuisine && (
                <p className="text-white/60 text-[10px] leading-tight truncate italic">{p.meta.cuisine}</p>
              )}
              {p.meta?.openNow === true && (
                <p className="text-[hsl(140_70%_75%)] text-[9px] font-semibold leading-tight mt-0.5">● abierto ahora</p>
              )}
              {p.meta?.openNow === false && (
                <p className="text-[hsl(0_70%_80%)] text-[9px] font-semibold leading-tight mt-0.5">● cerrado</p>
              )}
            </div>
          </button>
        );
      })}

      {/* No POIs in view hint */}
      {canShowAR && gpsReady && heading != null && annotated.length > 0 && visible.length === 0 && (
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center text-white/80 pointer-events-none">
          <Compass size={28} className="mx-auto mb-2 opacity-70" />
          <p className="text-sm">Girá el teléfono para encontrar lugares</p>
          <p className="text-xs text-white/60 mt-1">{annotated.length} cerca · el más próximo está a {formatDistance(annotated[0].distance)}</p>
        </div>
      )}

      {/* No POIs within range at all */}
      {canShowAR && gpsReady && pois.length > 0 && annotated.length === 0 && (
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center text-white/80 pointer-events-none">
          <p className="text-sm">Ningún punto del viaje a menos de {MAX_DISTANCE_M / 1000} km</p>
          <p className="text-xs text-white/60 mt-1">El modo AR funciona mejor cuando estás en destino.</p>
        </div>
      )}

      {/* Navigation arrow bottom bar (only when a target is set) */}
      {canShowAR && targetPoi && heading != null && (
        <div className="absolute left-0 right-0 bottom-0 px-4 pt-4 pb-[max(env(safe-area-inset-bottom),20px)] z-20" data-testid="ar-target-bar">
          <div className="rounded-2xl bg-black/75 backdrop-blur-md border border-white/15 text-white px-4 py-3 flex items-center gap-3">
            <div
              className="w-14 h-14 rounded-full bg-[hsl(var(--accent))] text-white flex items-center justify-center shrink-0 shadow-lg"
              style={{ transform: `rotate(${angularDelta(targetPoi.bearing, heading) || 0}deg)` }}
            >
              <Navigation size={24} fill="currentColor" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] uppercase tracking-wider text-white/60">Dirigite a</p>
              <p className="text-sm font-bold truncate">{targetPoi.name}</p>
              <p className="text-xs text-white/70 tabular-nums">{formatDistance(targetPoi.distance)} · {walkingMinutes(targetPoi.distance)} min a pie</p>
            </div>
            <button
              onClick={capturePhoto}
              className="w-14 h-14 rounded-full bg-white text-black flex items-center justify-center shrink-0 shadow-lg active:scale-95 transition-transform border-4 border-white/40"
              aria-label="Sacar foto"
              data-testid="ar-shutter-btn"
            >
              <Camera size={22} />
            </button>
          </div>
        </div>
      )}

      {/* Shutter button (bottom center, hidden when target bar is active since shutter moves into it) */}
      {canShowAR && !targetPoi && (
        <div
          className="absolute left-0 right-0 bottom-0 pb-[max(env(safe-area-inset-bottom),20px)] flex items-center justify-center z-20 pointer-events-none"
          data-testid="ar-shutter-wrap"
        >
          <button
            onClick={capturePhoto}
            className="pointer-events-auto w-[72px] h-[72px] rounded-full bg-white/95 border-[6px] border-white/35 shadow-xl active:scale-95 transition-transform flex items-center justify-center"
            aria-label="Sacar foto AR"
            data-testid="ar-shutter-btn"
          >
            <Camera size={28} className="text-black" />
          </button>
        </div>
      )}

      {/* Capture preview + share modal */}
      {capture && (
        <div className="fixed inset-0 z-[110] bg-black/85 backdrop-blur-sm flex flex-col" data-testid="ar-capture-modal">
          <div className="flex-1 flex items-center justify-center p-4 overflow-hidden">
            <img
              src={capture.url}
              alt="Captura AR"
              className="max-w-full max-h-full rounded-2xl shadow-2xl object-contain"
              data-testid="ar-capture-preview"
            />
          </div>

          {/* Story picker — only if any visible POI has a story */}
          {capture.poisSnap?.some((p) => p.story) && (
            <div className="px-3 pb-2" data-testid="ar-story-picker">
              <p className="text-[10px] uppercase tracking-wider text-white/60 px-1 mb-1.5 font-semibold">Agregar historia del lugar</p>
              <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-1">
                <button
                  onClick={() => setStoryPoi(null)}
                  className={`shrink-0 h-9 px-3 rounded-full border text-xs font-semibold transition-colors ${
                    !capture.storyPoiId
                      ? "bg-white text-black border-white"
                      : "bg-white/10 text-white border-white/20"
                  }`}
                  data-testid="ar-story-none"
                >
                  Sin historia
                </button>
                {capture.poisSnap.filter((p) => p.story).map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setStoryPoi(p.id)}
                    className={`shrink-0 h-9 px-3 rounded-full border text-xs font-semibold flex items-center gap-1.5 transition-colors ${
                      capture.storyPoiId === p.id
                        ? "bg-[hsl(var(--accent))] text-white border-[hsl(var(--accent))]"
                        : "bg-white/10 text-white border-white/20"
                    }`}
                    data-testid={`ar-story-poi-${p.id}`}
                  >
                    <span>{p.icon}</span>
                    <span className="max-w-[140px] truncate">{p.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="px-4 pb-[max(env(safe-area-inset-bottom),20px)] pt-2 flex items-center gap-2">
            <button
              onClick={discardPhoto}
              className="h-12 px-4 rounded-full bg-white/10 border border-white/20 text-white text-sm font-semibold flex items-center gap-2 active:scale-95"
              data-testid="ar-capture-retake"
            >
              <RotateCcw size={16} /> Repetir
            </button>
            <div className="flex-1" />
            <button
              onClick={downloadPhoto}
              className="h-12 w-12 rounded-full bg-white/10 border border-white/20 text-white flex items-center justify-center active:scale-95"
              aria-label="Descargar"
              data-testid="ar-capture-download"
            >
              <Download size={18} />
            </button>
            <button
              onClick={sharePhoto}
              disabled={sharing}
              className="h-12 px-5 rounded-full bg-[hsl(var(--accent))] text-white text-sm font-bold flex items-center gap-2 active:scale-95 disabled:opacity-60 shadow-lg"
              data-testid="ar-capture-share"
            >
              <Share2 size={16} /> {sharing ? "Compartiendo…" : "Compartir"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
